import React, { useCallback, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    CircularProgress,
    Stack,
    TextField,
} from "@mui/material";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";

import { changePassword } from "../../../api";
import { isKeycloakMode, startKeycloakPasswordChange } from "../../../auth/keycloakClient";
import { logError } from "../../../logging/loggingService";

export default function PasswordCard({ onNotify }) {
    const keycloakMode = isKeycloakMode();
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    });
    const [passwordErrors, setPasswordErrors] = useState({});
    const [passwordSubmitting, setPasswordSubmitting] = useState(false);
    const [keycloakRedirecting, setKeycloakRedirecting] = useState(false);

    const handleKeycloakPasswordChange = useCallback(async () => {
        setKeycloakRedirecting(true);
        try {
            await startKeycloakPasswordChange();
        } catch (err) {
            logError("Fehler beim Starten des Keycloak-Passwort-Workflows", err, { component: "Account" });
            onNotify("Keycloak-Passwort-Workflow konnte nicht gestartet werden.", "error");
        } finally {
            setKeycloakRedirecting(false);
        }
    }, [onNotify]);

    const validatePasswordForm = useCallback(() => {
        const errors = {};
        if (!passwordForm.currentPassword.trim()) {
            errors.currentPassword = "Bitte aktuelles Passwort angeben.";
        }
        if (!passwordForm.newPassword.trim()) {
            errors.newPassword = "Bitte neues Passwort angeben.";
        } else if (passwordForm.newPassword.trim().length < 8) {
            errors.newPassword = "Neues Passwort muss mindestens 8 Zeichen lang sein.";
        }
        if (passwordForm.newPassword && passwordForm.newPassword === passwordForm.currentPassword) {
            errors.newPassword = "Neues Passwort muss sich vom aktuellen unterscheiden.";
        }
        if (!passwordForm.confirmPassword.trim()) {
            errors.confirmPassword = "Bitte Passwortbestätigung eingeben.";
        } else if (passwordForm.confirmPassword !== passwordForm.newPassword) {
            errors.confirmPassword = "Passwörter stimmen nicht überein.";
        }
        setPasswordErrors(errors);
        return Object.keys(errors).length === 0;
    }, [passwordForm]);

    const clearPasswordError = useCallback((field) => {
        setPasswordErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    }, []);

    const handlePasswordSubmit = useCallback(
        async (event) => {
            event.preventDefault();
            if (!validatePasswordForm()) {
                return;
            }
            setPasswordSubmitting(true);
            try {
                const payload = {
                    currentPassword: passwordForm.currentPassword,
                    newPassword: passwordForm.newPassword,
                };
                const res = await changePassword(payload);
                if (res.status >= 200 && res.status < 300) {
                    onNotify("Passwort erfolgreich geändert.", "success");
                    setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
                    setPasswordErrors({});
                } else {
                    const message =
                        res.data && typeof res.data === "string" && res.data.trim().length > 0
                            ? res.data
                            : "Passwort konnte nicht geändert werden.";
                    onNotify(message, "error");
                }
            } catch (err) {
                logError("Fehler beim Ändern des Passworts", err, { component: "Account" });
                const backendError = err?.response?.data;
                const details = Array.isArray(backendError?.details) ? backendError.details : null;
                if (details && details.length > 0) {
                    const nextErrors = {};
                    details.forEach((detail) => {
                        const rawField = detail?.field;
                        const message = detail?.message;
                        if (!rawField || !message) return;
                        const field = rawField.split(".").pop();
                        nextErrors[field] = message;
                    });
                    if (Object.keys(nextErrors).length > 0) {
                        setPasswordErrors(nextErrors);
                        onNotify(backendError?.message || "Bitte prüfe die markierten Felder.", "error");
                        return;
                    }
                }
                const backendMessage = backendError;
                const message =
                    typeof backendMessage === "string" && backendMessage.trim().length > 0
                        ? backendMessage
                        : "Passwort konnte nicht geändert werden.";
                onNotify(message, "error");
            } finally {
                setPasswordSubmitting(false);
            }
        },
        [onNotify, passwordForm, validatePasswordForm]
    );

    return (
        <Card sx={{ borderRadius: 3, boxShadow: "0 8px 28px rgba(0,0,0,0.06)" }}>
            <CardHeader
                title="Passwort ändern"
                subheader={keycloakMode
                    ? "Im Keycloak-Modus wird die Passwortänderung zentral über Keycloak durchgeführt."
                    : "Sorge für einen sicheren Zugang zu deinem Account"}
                avatar={<LockResetRoundedIcon color="action" />}
            />
            <CardContent>
                {keycloakMode ? (
                    <Stack spacing={2}>
                        <Alert severity="info">
                            Die Passwortänderung erfolgt über die Keycloak-Anmeldeseite.
                        </Alert>
                        <CardActions sx={{ justifyContent: "flex-end", px: 0 }}>
                            <Button
                                variant="contained"
                                onClick={handleKeycloakPasswordChange}
                                disabled={keycloakRedirecting}
                                sx={{ bgcolor: "orange", "&:hover": { bgcolor: "#cc7a00" }, borderRadius: 2, px: 3, color: "#000" }}
                            >
                                {keycloakRedirecting ? <CircularProgress size={22} color="inherit" /> : "In Keycloak ändern"}
                            </Button>
                        </CardActions>
                    </Stack>
                ) : (
                <Box component="form" onSubmit={handlePasswordSubmit} noValidate>
                    <Stack spacing={2}>
                        <TextField
                            label="Aktuelles Passwort"
                            type="password"
                            autoComplete="current-password"
                            value={passwordForm.currentPassword}
                            onChange={(e) => {
                                setPasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }));
                                clearPasswordError("currentPassword");
                            }}
                            error={Boolean(passwordErrors.currentPassword)}
                            helperText={passwordErrors.currentPassword}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Neues Passwort"
                            type="password"
                            autoComplete="new-password"
                            value={passwordForm.newPassword}
                            onChange={(e) => {
                                setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }));
                                clearPasswordError("newPassword");
                            }}
                            error={Boolean(passwordErrors.newPassword)}
                            helperText={passwordErrors.newPassword || "Mindestens 8 Zeichen"}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Neues Passwort bestätigen"
                            type="password"
                            autoComplete="new-password"
                            value={passwordForm.confirmPassword}
                            onChange={(e) => {
                                setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }));
                                clearPasswordError("confirmPassword");
                            }}
                            error={Boolean(passwordErrors.confirmPassword)}
                            helperText={passwordErrors.confirmPassword}
                            fullWidth
                            required
                        />
                    </Stack>
                    <CardActions sx={{ justifyContent: "flex-end", px: 0, mt: 2 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={passwordSubmitting}
                            sx={{ bgcolor: "orange", "&:hover": { bgcolor: "#cc7a00" }, borderRadius: 2, px: 3, color: "#000" }}
                        >
                            {passwordSubmitting ? (
                                <CircularProgress size={22} color="inherit" />
                            ) : (
                                "Passwort speichern"
                            )}
                        </Button>
                    </CardActions>
                </Box>
                )}
            </CardContent>
        </Card>
    );
}
